import { Seo } from '@/seo/Seo'
import { pageJsonLd } from '@/seo/structuredData'
import { LegalLayout } from './LegalLayout'

/**
 * Privacy Policy. NOTE TO TEAM: this is a structural starter draft authored to
 * satisfy the Google Ads / Meta landing-page requirement and the site's privacy
 * surface — it is NOT legal advice. Have counsel review and finalize the body
 * text before relying on it. Once approved, treat the wording as verbatim.
 */
export function PrivacyPage() {
  return (
    <>
      <Seo
        title="Privacy Policy — Zenzers Geriatric Remote Monitoring"
        description="How Zenzers collects, uses, and protects personal and health data on its remote monitoring platform — HIPAA, Law 25, and GDPR."
        path="/privacy"
        jsonLd={pageJsonLd({
          path: '/privacy',
          name: 'Privacy Policy',
          description: 'How Zenzers collects, uses, and protects personal and health data.',
        })}
      />
      <LegalLayout title="Privacy Policy" updated="June 10, 2026">
        <p>
          Zenzers Inc. (&ldquo;Zenzers,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) provides a
          geriatric remote monitoring platform. This policy explains what information we collect,
          why we collect it, how long we keep it, who we share it with, and the rights you have.
        </p>

        <h2>Information we collect</h2>
        <ul>
          <li><strong>Account data</strong> — name, email, and role (patient, caregiver, clinician) used to create and secure your account.</li>
          <li><strong>Health &amp; device data</strong> — vitals, fall events, and location signals captured by Zenzers hardware, processed to deliver monitoring and alerts.</li>
          <li><strong>Order data</strong> — products purchased and subscription status. Payments are processed by our payment provider; we do not store full card numbers.</li>
          <li><strong>Usage data</strong> — limited technical logs needed to operate and secure the service. Analytics and advertising data are collected only after you opt in (see Consent).</li>
        </ul>

        <h2>Why we collect it</h2>
        <p>
          To provide monitoring and caregiver alerts, fulfill orders, secure accounts, meet legal
          and clinical obligations, and — only with consent — to measure and improve the service.
        </p>

        <h2>Retention</h2>
        <p>
          We keep personal and health data for as long as your account is active and as required by
          applicable health-records and tax law, then delete or de-identify it.
        </p>

        <h2>Who we share it with</h2>
        <p>
          We share data only with processors needed to run the service — our payment provider for
          checkout, our identity provider for sign-in, and (after consent) analytics providers. We
          do not sell personal information.
        </p>

        <h3 id="hipaa">HIPAA</h3>
        <p>
          Where Zenzers handles protected health information (PHI) on behalf of a covered entity, we
          act as a Business Associate and operate under a Business Associate Agreement (BAA), with
          administrative, physical, and technical safeguards consistent with the HIPAA Security Rule.
        </p>

        <h2>Consent &amp; cookies</h2>
        <p>
          No analytics or advertising cookies load before you opt in. We use Google Consent Mode v2:
          ad and analytics storage are denied by default and enabled only when you accept, in line
          with Quebec&rsquo;s Law 25, PIPEDA, and the GDPR.
        </p>

        <h2>Your rights</h2>
        <p>
          Subject to applicable law, you may request access to, correction of, or deletion of your
          personal data, and may withdraw consent at any time. Contact us at{' '}
          <a href="mailto:privacy@zenzers4life.com">privacy@zenzers4life.com</a>.
        </p>

        <h2>Contact</h2>
        <p>
          Questions about this policy: <a href="mailto:privacy@zenzers4life.com">privacy@zenzers4life.com</a>.
        </p>
      </LegalLayout>
    </>
  )
}
